import { z } from 'zod';
import { ChannelIdSchema, PartySlugSchema, PasskeySchema, SuggesterTokenSchema, TrackIdSchema, } from './ids.js';
import { SpotifyTrackRefSchema } from './spotify.js';
import { PollSyncSchema } from './sync.js';
import { IMAGE_URL_MAX_LENGTH } from './track.js';
export const CHANNEL_SLOTS_PER_PARTY = 3;
export const CHANNEL_COLOUR_MAX_LENGTH = 16;
// Single source for both the API's idle sweep and the UI copy describing it — the two must agree.
export const PARTY_IDLE_TIMEOUT_MS = 10 * 60 * 1000;
// How long a just-created, DJ-less party stays pinned at the top of the browse list.
export const PARTY_RECENTLY_CREATED_WINDOW_MS = 10 * 60 * 1000;
export const CHANNEL_DISPLAY_NAME_MAX_LENGTH = 64;
export const PARTY_NAME_MAX_LENGTH = 80;
/**
 * How many past parties `GET /api/parties/past` hands back — enough to
 * recognise a name in, nowhere near a full archive. Written down here because
 * both ends have to know the list is bounded for it to be read honestly.
 */
export const PAST_PARTIES_LIMIT = 50;
/**
 * Every path segment the app itself answers on, and so the only names a party
 * cannot have. Two families sit in one list because a name has to clear both:
 * `api`, `assets` and `party` are what the site serves at its root, and `past`,
 * `mine` and `resolve` are the API's own leaves under `/api/parties/`, which a
 * party of one of those names would be fetched as instead of its own view.
 *
 * The list stays this short because everything else the app serves nests
 * *under* a party — `/<slug>/dj/<id>`, `/<slug>/suggest` — so it costs the name
 * space almost nothing. Anything served at the root with a dot in it
 * (`favicon.ico`, `index.html`) is already unable to collide, since a slug
 * cannot contain one.
 *
 * `party` is here only while the old `/party/<slug>` links are still being
 * redirected; drop it once they are gone.
 */
export const RESERVED_PARTY_SLUGS = ['api', 'assets', 'party', 'past', 'mine', 'resolve'];
/**
 * A whole segment, not a prefix. nginx routes `/api/` and `/assets/` on a
 * complete segment as well, so `partygoers` is nobody's path but its own —
 * where the old prefix rule refused every real name that merely started with
 * one of these.
 */
const RESERVED_PARTY_SLUG_SET = new Set(RESERVED_PARTY_SLUGS);
export const isReservedPartySlug = (slug) => RESERVED_PARTY_SLUG_SET.has(slug);
/** A cover image URL, when Spotify has one for the thing in question. */
export const ImageUrlSchema = z.url().max(IMAGE_URL_MAX_LENGTH).nullable();
/** A track's name and who made it — the two fields every track shape carries. */
export const TitleArtistSchema = z.strictObject({
    title: z.string().min(1),
    artist: z.string().min(1),
});
/** A channel's UI colour, shared by every shape that carries one. */
export const ColourSchema = z.string().min(1).max(CHANNEL_COLOUR_MAX_LENGTH);
/** Every party has exactly `CHANNEL_SLOTS_PER_PARTY` fixed slots — one per literal channel colour. */
const ChannelSlotIdSchema = z.coerce
    .number()
    .int()
    .min(1)
    .max(CHANNEL_SLOTS_PER_PARTY)
    .pipe(ChannelIdSchema);
// connected reflects a live Spotify poll for this DJ.
// An expired or taken-over session reads disconnected, with queue/now-playing dropped rather than stale.
export const ChannelSchema = z
    .strictObject({
    id: ChannelIdSchema,
    displayName: z.string().min(1).max(CHANNEL_DISPLAY_NAME_MAX_LENGTH),
    colour: ColourSchema,
    connected: z.boolean(),
    /**
     * When the DJ currently holding this slot took it, so a viewer's own clock
     * can count the running stint out rather than the server re-sending a
     * number that is wrong the moment it is sent. Null once nobody is accruing
     * time on the slot — it was left, taken over, or the party is over.
     */
    onSetSince: z.iso.datetime().nullable(),
    /**
     * Time this DJ has already banked at *this party*, across every stint they
     * have finished on any of its channels. Added to the running stint above,
     * it is the answer to "how long have they been on tonight?" — which is not
     * the same as how long they have held this slot: a DJ who steps off and
     * comes back keeps their total, and whoever takes a slot over starts at
     * zero.
     */
    onSetMinutes: z.number().int().nonnegative(),
})
    .readonly();
// art is the album name, used only to derive a placeholder tile colour; imageUrl is the real cover, when Spotify has one.
export const ChannelNowPlayingSchema = TitleArtistSchema.extend({
    art: z.string().min(1),
    imageUrl: ImageUrlSchema,
    positionSeconds: z.number().int().nonnegative(),
    durationSeconds: z.number().int().positive(),
    playing: z.boolean(),
}).readonly();
const QueuedTrackBaseSchema = TitleArtistSchema.extend({
    id: TrackIdSchema,
    imageUrl: ImageUrlSchema,
    // Absolute instant, not a countdown: each channel samples on its own clock, so a countdown would reorder the merged queue every poll.
    // Null when not playing — a stopped deck has no honest ETA.
    startsAt: z.iso.datetime().nullable(),
});
export const QueuedTrackSchema = QueuedTrackBaseSchema.readonly();
// clashChannelId excludes this DJ's own channel and is only set behind the earliest copy of the track; playingNowChannelId includes it.
export const DjQueuedTrackSchema = QueuedTrackBaseSchema.extend({
    playedBefore: z.boolean(),
    clashChannelId: ChannelIdSchema.nullable(),
    playingNowChannelId: ChannelIdSchema.nullable(),
}).readonly();
const MINUTE_MS = 60_000;
const MINUTES_PER_HOUR = 60;
// Ties are left at 0 deliberately: callers merging several channels break them by channel, then queue position.
export const byStartTime = (a, b) => {
    const at = a.startsAt === null ? Infinity : new Date(a.startsAt).getTime();
    const bt = b.startsAt === null ? Infinity : new Date(b.startsAt).getTime();
    if (at === bt) {
        return 0;
    }
    return at < bt ? -1 : 1;
};
export const formatStartsIn = (startsAt, nowMs) => {
    if (startsAt === null) {
        return 'queued';
    }
    const etaMs = new Date(startsAt).getTime() - nowMs;
    if (!Number.isFinite(etaMs) || etaMs <= 0) {
        return 'now';
    }
    const minutes = Math.round(etaMs / MINUTE_MS);
    return minutes < 1 ? 'under a min' : `in ${String(minutes)} min`;
};
export const formatTimeOnSet = (channel, nowMs) => {
    const startedAtMs = channel.onSetSince === null ? NaN : new Date(channel.onSetSince).getTime();
    const runningMs = Number.isFinite(startedAtMs) ? Math.max(0, nowMs - startedAtMs) : 0;
    const total = channel.onSetMinutes + Math.floor(runningMs / MINUTE_MS);
    const hours = Math.floor(total / MINUTES_PER_HOUR);
    const minutes = total % MINUTES_PER_HOUR;
    return hours === 0
        ? `${String(minutes)}m`
        : `${String(hours)}h ${String(minutes).padStart(2, '0')}m`;
};
// Scoped to this party only: a DJ's plays elsewhere, or off-app, never appear here.
// playedAt is a server-timezone "HH:MM".
export const PlayedTrackSchema = TitleArtistSchema.extend({
    id: TrackIdSchema,
    imageUrl: ImageUrlSchema,
    playedAt: z.string().min(1).max(8),
}).readonly();
// Surfaced on the view, not just logs, so a stalled channel shows why. retryAt is Spotify's own backoff, not one we compute.
export const RateLimitSchema = z
    .strictObject({
    since: z.iso.datetime(),
    retryAt: z.iso.datetime(),
})
    .readonly();
// Repeated suggestions collapse into this one row (suggestedCount, refreshed age) instead of duplicating.
// playedBefore: already played this party. clashChannelId: queued on another channel. playingNowChannelId: on a deck right now.
export const CrowdWantSchema = TitleArtistSchema.extend({
    id: TrackIdSchema,
    // Nullable: a track without artwork is still worth suggesting; the tile falls back to a generated pattern.
    artUrl: z.url().nullable(),
    // Built server-side from the stored track id so the link can't drift; null for wants recorded before this field existed.
    trackUrl: z.url().nullable(),
    ageMinutes: z.number().int().nonnegative(),
    suggestedCount: z.number().int().min(1),
    playedBefore: z.boolean(),
    clashChannelId: ChannelIdSchema.nullable(),
    playingNowChannelId: ChannelIdSchema.nullable(),
}).readonly();
// A suggestion a DJ has just taken onto their own queue. It is gone from `wants` the moment it is
// taken; this is the only trace, and it is short-lived — see TAKEN_NOTICE_WINDOW_MS.
export const TakenWantSchema = TitleArtistSchema.extend({
    id: TrackIdSchema,
    channelId: ChannelIdSchema,
    channelLabel: z.string().min(1),
    channelColour: ColourSchema,
    takenAt: z.iso.datetime(),
}).readonly();
const ChannelPlaybackSchema = z.strictObject({
    nowPlaying: ChannelNowPlayingSchema.nullable(),
    next: z.array(QueuedTrackSchema).readonly(),
    played: z.array(PlayedTrackSchema).readonly(),
    rateLimit: RateLimitSchema.nullable(),
});
export const PublicChannelSchema = ChannelPlaybackSchema.extend({
    id: ChannelIdSchema,
    colour: ColourSchema,
    channel: ChannelSchema.nullable(),
}).readonly();
export const PartyViewSchema = z
    .strictObject({
    slug: PartySlugSchema,
    displayName: z.string().min(1),
    // Null while running; once set, channels stop being polled, so now-playing/queues read empty rather than stale.
    endedAt: z.iso.datetime().nullable(),
    /**
     * The name of the DJ who called it over, as the room can be shown it —
     * their display name at the moment they pressed the button, not a name
     * that follows whoever holds a channel afterwards. Null whenever `endedAt`
     * is null, and null for a party the idle sweep ended: nobody ended that
     * one, so it is "the party went quiet" rather than an unknown someone.
     */
    endedBy: z.string().min(1).max(CHANNEL_DISPLAY_NAME_MAX_LENGTH).nullable(),
    channels: z.array(PublicChannelSchema).readonly(),
    sync: PollSyncSchema,
})
    .readonly();
export const OtherChannelSchema = ChannelPlaybackSchema.extend({
    channel: ChannelSchema,
}).readonly();
// Carries the passkey: a DJ console can always reveal it to the room, so exposing it here isn't a leak.
export const DjViewSchema = z
    .strictObject({
    partySlug: PartySlugSchema,
    partyDisplayName: z.string().min(1),
    passkey: PasskeySchema,
    endedAt: z.iso.datetime().nullable(),
    /** See `PartyViewSchema.endedBy`. Null unless a person ended this party. */
    endedBy: z.string().min(1).max(CHANNEL_DISPLAY_NAME_MAX_LENGTH).nullable(),
    myChannel: ChannelSchema,
    myNowPlaying: ChannelNowPlayingSchema.nullable(),
    myQueue: z.array(DjQueuedTrackSchema).readonly(),
    myPlayed: z.array(PlayedTrackSchema).readonly(),
    // Set while Spotify is rate-limiting this DJ's own account — the one case the console shows stale numbers through no fault of theirs.
    myRateLimit: RateLimitSchema.nullable(),
    wants: z.array(CrowdWantSchema).readonly(),
    // Only takes inside TAKEN_NOTICE_WINDOW_MS, so a console that has been open all night is not
    // holding the whole night's history of them.
    recentlyTaken: z.array(TakenWantSchema).readonly(),
    otherChannels: z.array(OtherChannelSchema).readonly(),
    sync: PollSyncSchema,
})
    .readonly();
export const PartySlugParamsSchema = z
    .strictObject({
    slug: PartySlugSchema,
})
    .readonly();
export const ChannelParamsSchema = z
    .strictObject({
    slug: PartySlugSchema,
    channelId: ChannelSlotIdSchema,
})
    .readonly();
export const DismissWantParamsSchema = z
    .strictObject({
    slug: PartySlugSchema,
    channelId: ChannelSlotIdSchema,
    wantId: TrackIdSchema,
})
    .readonly();
// Taking a want names exactly what dismissing one does — one want, on one channel the caller
// must hold — so the two share a schema rather than keeping two copies of it in step.
export const QueueWantParamsSchema = DismissWantParamsSchema;
// A DJ adding a track they searched for themselves, so there is no want id to name — the track is
// the whole request, exactly as it is for a suggester.
export const DjEnqueueRequestSchema = z
    .strictObject({
    track: SpotifyTrackRefSchema,
})
    .readonly();
export const ResolvePartyQuerySchema = z
    .strictObject({
    name: z.string().trim().min(1).max(PARTY_NAME_MAX_LENGTH),
})
    .readonly();
export const ResolvePartyResponseSchema = z
    .strictObject({
    slug: PartySlugSchema,
    party: PartyViewSchema.nullable(),
})
    .readonly();
const DjCredentialsSchema = z.strictObject({
    displayName: z.string().trim().min(1).max(CHANNEL_DISPLAY_NAME_MAX_LENGTH),
});
// Ordered server-side: just-created & DJ-less, then DJ-live, then DJ-less.
// expiresAt is the actual idle-sweep deadline, not an estimate from play history.
export const ActivePartySchema = z
    .strictObject({
    slug: PartySlugSchema,
    displayName: z.string().min(1),
    djCount: z.number().int().min(0).max(CHANNEL_SLOTS_PER_PARTY),
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
})
    .readonly();
export const ActivePartiesResponseSchema = z.array(ActivePartySchema).readonly();
export const CreatePartyRequestSchema = z
    .strictObject({
    partyName: z.string().trim().min(1).max(PARTY_NAME_MAX_LENGTH),
})
    .readonly();
export const CreatePartyResponseSchema = z
    .strictObject({
    slug: PartySlugSchema,
    displayName: z.string().min(1),
})
    .readonly();
export const JoinChannelRequestSchema = DjCredentialsSchema.readonly();
// Always succeeds once called — the yes/no confirmation is client-side only; this evicts whoever was there unconditionally.
export const TakeoverChannelRequestSchema = DjCredentialsSchema.readonly();
export const JoinPartyResponseSchema = z
    .strictObject({
    partySlug: PartySlugSchema,
    partyDisplayName: z.string().min(1),
    passkey: PasskeySchema,
    channel: ChannelSchema,
})
    .readonly();
export const VerifyPasskeyRequestSchema = z
    .strictObject({
    passkey: z.string().trim().pipe(PasskeySchema),
})
    .readonly();
export const VerifyPasskeyResponseSchema = z
    .strictObject({
    token: SuggesterTokenSchema,
})
    .readonly();
/**
 * One party that has already happened, for the "names already used" list on
 * the create form. `endedAt` is null for a party that is still running.
 */
export const PastPartySchema = z
    .strictObject({
    slug: PartySlugSchema,
    displayName: z.string().min(1),
    createdAt: z.iso.datetime(),
    endedAt: z.iso.datetime().nullable(),
})
    .readonly();
/**
 * `GET /api/parties/past` — the most recent `PAST_PARTIES_LIMIT` parties,
 * newest first, rather than the whole archive: this is read by a person
 * scanning for a name they recognise, not by anything that needs every party
 * ever thrown.
 *
 * So a name absent from this list is not evidence it is free. Only
 * `POST /api/parties` can say that, and it answers 409 for a name already
 * used; anything that has to know before submitting asks
 * `GET /api/parties/resolve?name=...`, which looks the one name up instead of
 * searching a truncated list.
 */
export const PastPartiesResponseSchema = z.array(PastPartySchema).readonly();
/**
 * The party the signed-in DJ is currently on the decks at. A party that has
 * ended holds nobody, so this is null once it is over.
 */
export const MyPartySchema = z
    .strictObject({
    slug: PartySlugSchema,
    displayName: z.string().min(1),
    channelId: ChannelIdSchema,
})
    .readonly();
/** `GET /api/parties/mine` — which party, if any, the caller's session holds a channel at. */
export const MyPartyResponseSchema = z
    .strictObject({
    party: MyPartySchema.nullable(),
})
    .readonly();
//# sourceMappingURL=party.js.map